import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

import { LegfiCurrencyPipe } from '../../../../../pipes/format/legfi-currency.pipe';
import {
    TabularReportColumn,
    TabularReportRow,
} from '../../../../app-layout/shared/tabular-report/tabular-report.config';
import { ReferenceColumnComponent } from '../../../../app-layout/shared/tabular-report/reference-column/reference-column.component';

/**
 * V2 Layout hierarchical report table.
 *
 * Renders nested/accordion-style financial reports (Balance Sheet, P&L, etc.)
 * with expand/collapse, indentation levels, and currency formatting.
 *
 * Lives in v2-layout/reports/shared/ — reusable across all hierarchical reports.
 */
@Component({
    selector: 'app-report-table',
    templateUrl: './report-table.component.html',
    styleUrls: ['./report-table.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatIconModule,
        LegfiCurrencyPipe,
        ReferenceColumnComponent,
    ],
})
export class ReportTableComponent implements OnChanges {
    /** Raw hierarchical rows from the API (with children arrays) */
    @Input() rows: TabularReportRow[] = [];

    /** Column definitions from the API */
    @Input() columns: TabularReportColumn[] = [];

    /** Organization name (displayed centered above the table) */
    @Input() orgName = '';

    /** Report title (displayed in uppercase below org name) */
    @Input() reportTitle = '';

    /** Date subtitle (e.g. "As of 03/27/2026" or date range) */
    @Input() dateSubtitle = '';

    /** The column valueKey that should be clickable for navigation (e.g. 'label') */
    @Input() clickableCategoryColumn = '';

    /** When true, constrain max-width on wide screens (good for 2-col reports like budget summary) */
    @Input() compact = false;

    /**
     * When true, rows that are plain data leaves (no children, not category/subtotal/total)
     * ignore the hierarchy indent so their first column aligns with the table header.
     * Use for transaction-style reports (P&L Detail) where leaves carry actual column values.
     */
    @Input() unindentLeaves = false;

    /** Emits when a clickable row is clicked (e.g. account drill-through) */
    @Output() categoryClick = new EventEmitter<TabularReportRow>();

    /** Flattened rows for display */
    displayRows: TabularReportRow[] = [];

    /** Visible columns (display !== false) */
    visibleColumns: TabularReportColumn[] = [];

    /** The first column (label) — used for collapse icon */
    labelColumn: TabularReportColumn | null = null;

    /** Value columns (everything after label) */
    valueColumns: TabularReportColumn[] = [];

    constructor(private _cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['columns']) {
            this.visibleColumns = this.columns.filter(c => c.display !== false);
            this.labelColumn = this.visibleColumns.find(c => c.collapse) || this.visibleColumns[0] || null;
            this.valueColumns = this.visibleColumns.filter(c => c !== this.labelColumn);
        }
        if (changes['rows'] || changes['columns']) {
            this.buildDisplayRows();
        }
    }

    /** Toggle expand/collapse on a collapsible row */
    toggleRow(row: TabularReportRow): void {
        if (!row.collapsible) { return; }
        row.show = !row.show;
        this.buildDisplayRows();
        this._cdr.markForCheck();
    }

    /**
     * Check if a row's label is clickable for drill-through navigation.
     * Matches the old tabular-report logic exactly.
     */
    isRowClickable(row: TabularReportRow): boolean {
        if (!this.clickableCategoryColumn) { return false; }
        if (row.subTotal || row.grandTotal) { return false; }
        return !!(row.categoryId || row.accountId);
    }

    /** Handle click on a clickable label — emit the row for parent to navigate */
    onCategoryClick(event: Event, row: TabularReportRow): void {
        if (this.isRowClickable(row)) {
            event.stopPropagation(); // Prevent accordion toggle
            this.categoryClick.emit(row);
        }
    }

    /** Format a cell value based on column format */
    formatValue(row: TabularReportRow, column: TabularReportColumn): string | null {
        const val = row[column.valueKey];
        if (val == null) { return null; }
        if (column.hideZero && val === 0) { return null; }
        return val;
    }

    /** Whether a value should be formatted as currency */
    isMoney(column: TabularReportColumn): boolean {
        return column.format === 'money';
    }

    /** Whether a value should be rendered as a reference (type+id → dialog link) */
    isReference(column: TabularReportColumn): boolean {
        return column.format === 'reference';
    }

    /** Columns whose format is not money/percent — the "text" columns that total rows collapse. */
    private isTextColumn(column: TabularReportColumn): boolean {
        return !this.isMoney(column) && !this.isPercent(column);
    }

    /** A row is a subtotal/grand-total row if it's flagged as such. These rows only need to
     * display the label + money columns; text columns are blank and just create empty space. */
    private isTotalRow(row: TabularReportRow): boolean {
        return !!(row.subTotal || row.grandTotal);
    }

    /** For total rows, return only the money-format value columns (description column is
     * implicit in the widened label cell). For normal rows, return every value column. */
    valueColumnsForRow(row: TabularReportRow): TabularReportColumn[] {
        if (this.isTotalRow(row)) {
            return this.valueColumns.filter(c => !this.isTextColumn(c));
        }
        return this.valueColumns;
    }

    /** Flex-grow for the label cell so a total row's label spans through every text
     * value column that we skipped. Normal rows stay at 1 (matches existing layout). */
    labelFlexGrow(row: TabularReportRow): number {
        if (!this.isTotalRow(row)) { return 1; }
        const skippedTextCols = this.valueColumns.filter(c => this.isTextColumn(c)).length;
        return 1 + skippedTextCols;
    }

    /** Whether a value should be formatted as a percentage */
    isPercent(column: TabularReportColumn): boolean {
        return column.format === 'percent';
    }

    /** Whether a column has a numeric format (money, percent, number) — used for right-alignment */
    isNumericFormat(column: TabularReportColumn): boolean {
        return column.format === 'money' || column.format === 'percent' || column.format === 'number';
    }

    trackByIndex(index: number): number {
        return index;
    }

    /** Padding in px for the label cell */
    labelPadding(row: TabularReportRow): string {
        const level = row.level || 0;
        if (this.unindentLeaves && this.isDataLeaf(row) && level > 0) {
            return `${8 + (level - 1) * 20 + 24}px`;
        }
        return `${8 + level * 20}px`;
    }

    /** Negative margin-right to compensate for indent so value columns stay aligned */
    labelMarginRight(row: TabularReportRow): string {
        const level = row.level || 0;
        if (level === 0) { return '0'; }
        if (this.unindentLeaves && this.isDataLeaf(row)) {
            // Data leaves in unindentLeaves mode: compensate for the chevron-clearing indent
            return `-${(level - 1) * 20 + 24}px`;
        }
        // All other indented rows: compensate so value columns don't shift
        return `-${level * 20}px`;
    }

    private isDataLeaf(row: TabularReportRow): boolean {
        if (row.children?.length) { return false; }
        return !row.categoryHeader
            && !row.subTotal
            && !row.nestedTotal
            && !row.grandTotal
            && !row.collapsible;
    }

    /**
     * Flatten the hierarchical row tree into a display array.
     * Only includes children of rows where show !== false.
     */
    private buildDisplayRows(): void {
        this.displayRows = this.flattenRows(this.rows, 0);
    }

    private flattenRows(rows: TabularReportRow[], level: number): TabularReportRow[] {
        return rows.reduce<TabularReportRow[]>((result, row) => {
            row.level = level;
            const children = (row.children?.length && row.show !== false)
                ? this.flattenRows(row.children, level + 1)
                : [];
            return [...result, row, ...children];
        }, []);
    }
}
